import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import type { Order } from '../data/order';
import { orderUpdatedPayloadSchema } from '../data/order-ws';
import { orderQueryOptions } from '../query/query-options';
import { useConnectionStatus } from '../websocket/use-connection-status';
import { useStoreWebSocket } from '../websocket/use-store-websocket';
import { isOrderWriteInFlight } from './orders';

/**
 * Keep one order's query current from the WebSocket. Mirrors
 * useStoreEventListener: an `order_updated` push is a slim `{order_id, version}`
 * notification, never data — a newer version invalidates the query and
 * TanStack refetches through REST, so scrubbing and totals stay on one
 * serialization contract.
 *
 * The cache IS the version baseline. Every v2 writer lands its response
 * through writeOrderCache, so a push at or below the cached version is our own
 * echo or the per-reconnect duplicate.
 *
 * The one wrinkle is ordering: the server broadcasts before it answers the
 * request, so a tab's own echo routinely arrives ahead of its own response,
 * looking newer than the cache. Refetching then would be harmless (the query's
 * newerOrder guard keeps the response from regressing) but wasteful on every
 * keystroke flush — so while one of our writes is outstanding the push is
 * parked, and re-checked once the mutation settles. A successful response (or
 * a 409 rebase) has written a version at least that new by then and the park
 * drops; a failed write leaves the cache behind and the refetch is owed.
 *
 * A socket reconnect goes through the same gate. wsHello → webPostConnect
 * replays store_updated only, so an order_updated emitted while the socket was
 * down is lost and the reconnect owes one refetch — unless one of our own
 * writes answers first, since its response is the server's current order.
 */
export function useOrderEventListener(storeId: string, orderId: string): void {
  const { subscribe } = useStoreWebSocket();
  const queryClient = useQueryClient();

  // Highest version announced while one of our own writes was in flight.
  const parkedRef = useRef<number | null>(null);

  // `announced` is the version a push carried. A reconnect announces nothing
  // and stands for "anything above the cache"; the smallest such version is
  // enough to put it through the same park/re-check.
  const reconcile = useCallback(
    (announced?: number): void => {
      const queryKey = orderQueryOptions(storeId, orderId).queryKey;
      const cached = queryClient.getQueryData<Order>(queryKey);
      const version = announced ?? (cached?.version ?? 0) + 1;
      if (cached && version <= cached.version) {
        return;
      }

      if (isOrderWriteInFlight(queryClient, orderId)) {
        parkedRef.current = Math.max(parkedRef.current ?? 0, version);

        return;
      }

      void queryClient.invalidateQueries({ queryKey });
    },
    [queryClient, storeId, orderId]
  );

  useEffect(() => {
    const unsubscribePush = subscribe('order_updated', orderUpdatedPayloadSchema, (payload) => {
      // The server broadcasts once per changed draft to every tab on the store.
      if (payload.order_id !== orderId) {
        return;
      }

      reconcile(payload.version);
    });

    // Mutation onSuccess/onError (and so writeOrderCache) run before the
    // settled state is dispatched, so by the time this fires for our write the
    // cache already reflects its outcome.
    const unsubscribeMutations = queryClient.getMutationCache().subscribe(() => {
      const parked = parkedRef.current;
      if (parked === null) {
        return;
      }

      parkedRef.current = null;
      reconcile(parked);
    });

    return () => {
      unsubscribePush();
      unsubscribeMutations();
      parkedRef.current = null;
    };
  }, [subscribe, queryClient, orderId, reconcile]);

  // The pushes structurally cannot cover the socket dying while the network is
  // fine (API Gateway's 10-minute idle timeout, a proxy drop): navigator.onLine
  // stays true, so TanStack's refetchOnReconnect never fires. Deliberately NOT
  // wired into onlineManager — that is global and would pause every query and
  // mutation in the app. The mount's connecting → open is not a reconnect; the
  // mount fetch covers it.
  const connection = useConnectionStatus();
  const previousConnectionRef = useRef(connection);
  useEffect(() => {
    const previous = previousConnectionRef.current;
    previousConnectionRef.current = connection;
    if (previous === 'closed' && connection === 'open') {
      reconcile();
    }
  }, [connection, reconcile]);
}
